import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, X } from 'lucide-react';
import { backofficeFetch } from '@/services/backoffice-api';
import { DetailPanel, type PanelSpan } from './DetailPanelGrid';
import { getTagColorClassName, type TagColor } from './tag-colors';

type EntityType =
  'user' | 'project' | 'organization' | 'interview' | 'payment_transaction' | 'purchase';
type Tag = { id: string; name: string; color?: TagColor | null; status: 'active' | 'archived' };

interface EntityTagsResponse {
  items?: Array<{ tag: Tag }>;
}

interface TagsResponse {
  items?: Tag[];
}

export function TagsTile({
  entityType,
  entityId,
  panelId,
  currentSpan,
}: {
  entityType: EntityType;
  entityId: string;
  panelId?: string | undefined;
  currentSpan?: PanelSpan | undefined;
}) {
  const [tags, setTags] = useState<Tag[]>([]);
  const [catalog, setCatalog] = useState<Tag[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [mutatingTagId, setMutatingTagId] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadTags() {
      try {
        const [associated, available] = await Promise.all([
          backofficeFetch(`/entities/${entityType}/${entityId}/tags`),
          backofficeFetch('/tags?status=active'),
        ]);

        if (!isMounted) {
          return;
        }

        if (associated.ok) {
          const payload = (await associated.json()) as EntityTagsResponse;
          setTags((payload.items ?? []).map((item) => item.tag));
        } else {
          setTags([]);
        }

        if (available.ok) {
          const payload = (await available.json()) as TagsResponse;
          setCatalog(payload.items ?? []);
        } else {
          setCatalog([]);
        }
      } catch {
        if (isMounted) {
          setTags([]);
          setCatalog([]);
        }
      }
    }

    void loadTags();

    return () => {
      isMounted = false;
    };
  }, [entityId, entityType]);

  const attach = async (tagId: string) => {
    if (tags.some((tag) => tag.id === tagId)) {
      return;
    }

    setMutatingTagId(tagId);
    setMutationError(null);

    try {
      const response = await backofficeFetch(`/entities/${entityType}/${entityId}/tags/${tagId}`, {
        method: 'PUT',
      });
      if (response.ok) {
        const item = (await response.json()) as { tag: Tag };
        setTags((current) =>
          current.some((assigned) => assigned.id === item.tag.id) ? current : [...current, item.tag]
        );
        return;
      }

      setMutationError('Failed to add tag');
    } catch {
      setMutationError('Failed to add tag');
    } finally {
      setMutatingTagId(null);
    }
  };

  const detach = async (tagId: string) => {
    if (!tags.some((tag) => tag.id === tagId)) {
      return;
    }

    setMutatingTagId(tagId);
    setMutationError(null);

    try {
      const response = await backofficeFetch(`/entities/${entityType}/${entityId}/tags/${tagId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setTags((current) => current.filter((tag) => tag.id !== tagId));
        return;
      }

      setMutationError('Failed to remove tag');
    } catch {
      setMutationError('Failed to remove tag');
    } finally {
      setMutatingTagId(null);
    }
  };

  const assignedTagIds = new Set(tags.map((tag) => tag.id));
  const sortedTags = sortTagsByName(tags);

  return (
    <>
      <DetailPanel
        title="Tags"
        span="2x1"
        panelId={panelId}
        currentSpan={currentSpan}
        headerActions={
          <button
            type="button"
            aria-label="Edit tags"
            title="Edit tags"
            className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition hover:bg-backoffice-50 hover:text-backoffice-700"
            onClick={() => {
              setMutationError(null);
              setEditModalOpen(true);
            }}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </button>
        }
      >
        {sortedTags.length === 0 ? (
          <span className="text-sm text-neutral-500">No tags</span>
        ) : (
          <div className="flex flex-wrap gap-2">
            {sortedTags.map((tag) => (
              <span
                key={tag.id}
                className={[
                  'inline-flex items-center rounded-full border px-2 py-1 text-sm',
                  getTagColorClassName(tag.color),
                ].join(' ')}
              >
                {tag.name}
                {tag.status === 'archived' ? ' (archived)' : ''}
              </span>
            ))}
          </div>
        )}
      </DetailPanel>
      {editModalOpen ? (
        <EditTagsModal
          tags={sortTagsByName(catalog)}
          assignedTagIds={assignedTagIds}
          mutatingTagId={mutatingTagId}
          error={mutationError}
          onClose={() => setEditModalOpen(false)}
          onSelect={(tagId) => void attach(tagId)}
          onRemove={(tagId) => void detach(tagId)}
        />
      ) : null}
    </>
  );
}

function sortTagsByName(items: Tag[]): Tag[] {
  return [...items].sort((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
  );
}

function EditTagsModal({
  tags,
  assignedTagIds,
  mutatingTagId,
  error,
  onClose,
  onSelect,
  onRemove,
}: {
  tags: Tag[];
  assignedTagIds: Set<string>;
  mutatingTagId: string | null;
  error: string | null;
  onClose: () => void;
  onSelect: (tagId: string) => void;
  onRemove: (tagId: string) => void;
}) {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/40 p-4">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Close edit tags modal"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-tags-modal-title"
        className="relative z-10 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id="edit-tags-modal-title" className="text-lg font-semibold text-neutral-900">
              Edit tags
            </h2>
            <p className="mt-1 text-sm text-neutral-600">
              Select tags to add them. Selected tags can be removed with the x action.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition hover:bg-neutral-100 hover:text-neutral-800"
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {error ? (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {tags.length === 0 ? (
          <p className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-6 text-center text-sm text-neutral-500">
            No active tags available.
          </p>
        ) : (
          <div className="flex max-h-80 flex-wrap gap-2 overflow-y-auto">
            {tags.map((tag) => {
              const selected = assignedTagIds.has(tag.id);
              const disabled = Boolean(mutatingTagId);
              return (
                <button
                  key={tag.id}
                  type="button"
                  aria-pressed={selected}
                  aria-label={selected ? `Remove ${tag.name}` : `Add ${tag.name}`}
                  className={[
                    'inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm font-medium transition',
                    selected
                      ? `${getTagColorClassName(tag.color)} hover:brightness-95`
                      : 'border-neutral-200 bg-neutral-50 text-neutral-400 hover:border-neutral-300 hover:bg-neutral-100 hover:text-neutral-600',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={disabled}
                  onClick={() => {
                    if (selected) {
                      onRemove(tag.id);
                    } else {
                      onSelect(tag.id);
                    }
                  }}
                >
                  {tag.name}
                  {tag.status === 'archived' ? ' (archived)' : ''}
                  {selected ? <X className="h-3 w-3" aria-hidden="true" /> : null}
                  {mutatingTagId === tag.id ? (
                    <span className="sr-only">{selected ? 'Removing' : 'Adding'}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
